import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Scale, ShieldCheck, Clock, FileText, Landmark, Gavel, Users, Receipt, Globe, Lock, CheckCircle2, ChevronRight, Sparkles, Sun, Moon, Leaf } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import "./landing.css";

const THEMES = [
  { id: "jurisos", name: "JurisOS", icon: Sparkles },
  { id: "midnight", name: "Midnight", icon: Moon },
  { id: "parchment", name: "Parchment", icon: Sun },
  { id: "emerald", name: "Emerald", icon: Leaf },
];
const FEATURES = [
  { icon: Users, title: "CRM & KYC", text: "Verified identity, conflict checks and a single client record from first enquiry.", span: "lg:col-span-2" },
  { icon: FileText, title: "Cost agreements", text: "VLSB-compliant disclosure, e-signature and a hard gate before any invoice is raised." },
  { icon: Clock, title: "6-minute recording", text: "Units captured against the matter in one click, with WIP visible in real time." },
  { icon: Receipt, title: "GST tax invoices", text: "Approve, send, remind and reconcile. Card payments land straight into the ledger.", span: "lg:col-span-2" },
  { icon: Landmark, title: "Trust accounting", text: "Receipts, transfers and statements under the Uniform Law." },
  { icon: Gavel, title: "Court log", text: "Red-amber-green deadlines synced to Outlook, Google or Apple Calendar." },
  { icon: Globe, title: "Client portal", text: "Invoices, receipts, trust balance and documents in one secure place — with monthly statements and card payments built in.", span: "md:col-span-2 lg:col-span-4 lg:flex lg:items-center lg:gap-8" },
];
const STATS = [["1 DB", "per firm — fully isolated"], ["6 min", "billing unit"], ["14 days", "free trial"], ["AES-256", "encryption at rest"]];

export default function Login() {
  const [theme, setTheme] = useState(() => localStorage.getItem("legalos_theme") || "jurisos");
  useEffect(() => { localStorage.setItem("legalos_theme", theme); }, [theme]);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="landing min-h-screen" data-theme={theme} data-testid="landing-page">
      <div className="landing-grain" />
      <header className="landing-header" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--badge-bg)", border: "1px solid var(--border)" }}><Scale className="w-5 h-5" style={{ color: "var(--primary)" }} /></div>
            <div><p className="font-serif text-xl leading-none" style={{ color: "var(--text)" }}>JurisOS</p><p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--muted)" }}>Legal practice platform</p></div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm" style={{ color: "var(--muted)" }}>
            {[["features", "Platform"], ["firm", "For firms"], ["signin", "Sign in"]].map(([id, l]) => <button key={id} onClick={() => scrollTo(id)} className="landing-navlink" data-testid={`nav-${id}`}>{l}</button>)}
          </nav>
          <div className="flex items-center gap-1 p-1 rounded-full" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid="theme-switcher">
            {THEMES.map((t) => <button key={t.id} onClick={() => setTheme(t.id)} title={t.name} data-testid={`theme-${t.id}`} className={`theme-pill ${theme === t.id ? "active" : ""}`}><t.icon className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t.name}</span></button>)}
          </div>
        </div>
      </header>

      <section id="signin" className="relative overflow-hidden" data-testid="hero-section">
        <div className="hero-glow" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="overline mb-6"><Sparkles className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />Multi-tenant practice management for Australian law firms</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight max-w-2xl" style={{ color: "var(--text)" }}>
              From first enquiry to final invoice, <span className="italic" style={{ color: "var(--primary)" }}>every six minutes</span> accounted for.
            </h1>
            <p className="mt-7 text-base md:text-lg max-w-xl leading-relaxed" style={{ color: "var(--muted)" }}>
              JurisOS gives every law firm its own secure workspace — client intake, cost agreements, time, GST billing, trust accounting and court deadlines — under the firm's own brand.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button onClick={() => scrollTo("features")} className="btn-primary" data-testid="hero-explore-button">Explore the platform <ChevronRight className="w-4 h-4" /></button>
              <Link to="/onboarding" className="btn-ghost" data-testid="hero-onboard-button">Start your firm on JurisOS</Link>
            </div>
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid="hero-stats">
              {STATS.map(([v, l], i) => <motion.div key={l} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08 }}><p className="font-mono text-2xl" style={{ color: "var(--text)" }}>{v}</p><p className="text-xs uppercase tracking-wider mt-1" style={{ color: "var(--muted)" }}>{l}</p></motion.div>)}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}>
            <AuthCard />
          </motion.div>
        </div>
      </section>

      <section className="trust-strip" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>
          {["Legal Profession Uniform Law", "VLSB cost disclosure", "GST & trust accounting", "Bank-grade encryption", "MFA on every staff login", "Isolated database per firm"].map((t) => <span key={t} className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />{t}</span>)}
        </div>
      </section>

      <section id="features" className="max-w-7xl mx-auto px-6 py-24 lg:py-32" data-testid="feature-bento-grid">
        <p className="overline mb-4">The JurisOS platform</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold max-w-2xl leading-tight" style={{ color: "var(--text)" }}>Seven modules. One matter file. No gaps between them.</h2>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: (i % 4) * 0.07 }} className={`feature-card ${f.span || ""}`} data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center mb-6" style={{ background: "var(--badge-bg)", border: "1px solid var(--border)" }}><f.icon className="w-5 h-5" style={{ color: "var(--primary)" }} /></div>
              <h3 className="text-lg font-bold mb-2" style={{ color: "var(--text)" }}>{f.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="firm" className="relative" data-testid="firm-highlights">
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="firm-image" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1705326117801-2f27f5c6b512?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)" }} />
            <div className="firm-card" data-testid="firm-card">
              <p className="overline mb-2">Your firm here</p>
              <p className="font-serif text-2xl" style={{ color: "var(--text)" }}>Your name. Your logo. Your letterhead.</p>
              <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Every JurisOS workspace carries the firm's own brand — not ours.</p>
            </div>
          </div>
          <div>
            <p className="overline mb-4">For firms</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight" style={{ color: "var(--text)" }}>Your firm. Your brand. Our platform underneath.</h2>
            <div className="mt-10 space-y-6">
              {[["Your own workspace", "Each firm gets an isolated database, its own logo, ABN and letterhead on every invoice, agreement and email."],
                ["Cost agreement gate", "No invoice leaves the firm until the client has signed. Reminders go out automatically at five days."],
                ["Partner visibility", "Weekly billings, WIP, overdue and workload digests — plus the dashboard any morning."],
                ["Clients kept informed", "Receipts, monthly statements and a portal with every invoice, trust balance and document."]].map(([t, d]) => (
                <div key={t} className="flex gap-4">
                  <div className="mt-1 w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--badge-bg)", border: "1px solid var(--border)" }}><CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /></div>
                  <div><p className="font-semibold" style={{ color: "var(--text)" }}>{t}</p><p className="text-sm mt-1 leading-relaxed" style={{ color: "var(--muted)" }}>{d}</p></div>
                </div>))}
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row justify-between gap-6 text-xs" style={{ color: "var(--muted)" }}>
          <div className="flex items-center gap-3"><Scale className="w-4 h-4" style={{ color: "var(--primary)" }} /><span className="font-serif text-base" style={{ color: "var(--text)" }}>JurisOS</span><span>· The practice management platform for Australian law firms</span></div>
          <div className="flex items-center gap-6"><span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> Encrypted & MFA protected</span><Link to="/owner" className="landing-navlink" data-testid="platform-login-link">Platform owner console</Link><span>© {new Date().getFullYear()}</span></div>
        </div>
      </footer>
    </div>
  );
}

function AuthCard() {
  const [tab, setTab] = useState("staff");
  return (
    <div className="auth-card" data-testid="auth-card">
      <div className="flex p-1 rounded-lg mb-7" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
        {[["staff", "Staff sign in", ShieldCheck], ["client", "Client portal", Globe]].map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`${k}-tab-trigger`} className={`auth-tab ${tab === k ? "active" : ""}`}><I className="w-4 h-4" /> {l}</button>))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, x: tab === "staff" ? -12 : 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: tab === "staff" ? 12 : -12 }} transition={{ duration: 0.2 }}>
          {tab === "staff" ? <StaffForm /> : <ClientForm />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function StaffForm() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfa, setMfa] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submitCreds = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const r = await api.post("/auth/login", { email, password }); setMfa(r.data); setStep(2); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const submitMfa = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const r = await api.post("/auth/mfa/verify", { mfa_token: mfa.mfa_token, code });
      login(r.data.token, r.data.user, r.data.firm);
      toast.success(`Welcome back, ${r.data.user.name} · ${r.data.firm?.name || ""}`);
      nav(r.data.user.role === "support" ? "/clients" : "/");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  if (step === 2) return (
    <form onSubmit={submitMfa} className="space-y-5" data-testid="mfa-form">
      <p className="overline">Step 2 of 2</p>
      <h2 className="font-serif text-2xl md:text-3xl font-semibold" style={{ color: "var(--text)" }}>Verify your identity</h2>
      <div className="rounded-md p-4 flex gap-3 items-start text-sm" style={{ background: "var(--badge-bg)", border: "1px solid var(--border)", color: "var(--muted)" }}>
        <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
        <p data-testid="mfa-message">A 6-digit verification code has been issued for your account. <span data-testid="mfa-hint">{mfa?.hint}</span></p>
      </div>
      <div><label className="auth-label">Verification code</label>
        <input data-testid="login-mfa-code-input" className="auth-field font-mono tracking-[0.5em] text-center text-lg" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} required autoFocus /></div>
      {error && <p data-testid="mfa-error" className="auth-error">{error}</p>}
      <button type="submit" disabled={busy || code.length !== 6} className="btn-primary w-full justify-center h-11" data-testid="mfa-submit-button">{busy ? "Verifying…" : "Verify & sign in"}</button>
      <button type="button" onClick={() => { setStep(1); setCode(""); setError(""); }} className="text-xs w-full landing-navlink" data-testid="mfa-back-button">Use a different account</button>
    </form>
  );
  return (
    <form onSubmit={submitCreds} className="space-y-5" data-testid="login-form">
      <p className="overline">Firm staff access</p>
      <h2 className="font-serif text-2xl md:text-3xl font-semibold" style={{ color: "var(--text)" }}>Sign in to your firm</h2>
      <div><label className="auth-label">Username or email</label>
        <input data-testid="login-email-input" className="auth-field" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus autoComplete="username" /></div>
      <div><label className="auth-label">Password</label>
        <input data-testid="login-password-input" className="auth-field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" /></div>
      {error && <p data-testid="login-error" className="auth-error">{error}</p>}
      <button type="submit" disabled={busy} className="btn-primary w-full justify-center h-11" data-testid="login-submit-button">{busy ? "Checking…" : "Continue"}</button>
      <p className="text-[11px] text-center" style={{ color: "var(--muted)" }}>Multi-factor verification follows on the next step. New firm? <Link to="/onboarding" className="underline" data-testid="signin-onboard-link">Start a 14-day trial</Link>.</p>
    </form>
  );
}

function ClientForm() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const r = await api.post("/auth/client-login", { username, password }); toast.success(`Welcome, ${r.data.client_name}`); nav(`/portal/${r.data.portal_token}`); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="client-login-form">
      <p className="overline">Client portal</p>
      <h2 className="font-serif text-2xl md:text-3xl font-semibold" style={{ color: "var(--text)" }}>Your matters, invoices & trust</h2>
      <div><label className="auth-label">Client username</label>
        <input data-testid="client-username-input" className="auth-field" value={username} onChange={(e) => setUsername(e.target.value)} required autoComplete="username" /></div>
      <div><label className="auth-label">Password</label>
        <input data-testid="client-password-input" className="auth-field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" /></div>
      {error && <p data-testid="client-login-error" className="auth-error">{error}</p>}
      <button type="submit" disabled={busy} className="btn-primary w-full justify-center h-11" data-testid="client-login-submit-button">{busy ? "Signing in…" : "Open my portal"}</button>
      <p className="text-[11px] text-center" style={{ color: "var(--muted)" }}>Lost your details? Contact your lawyer and we'll resend your secure portal link.</p>
    </form>
  );
}
